import { inject, Injectable } from '@angular/core';

import {
  ARTICLE_INVENTORY_WARNINGS_QUERY,
  DISMISS_INVENTORY_JOB_MUTATION,
  INVENTORY_JOBS_QUERY,
  INVENTORY_JOB_COUNTS_QUERY,
  RETRY_INVENTORY_JOB_MUTATION,
} from '@core-graphql';
import { Apollo } from 'apollo-angular';
import { forkJoin, map, Observable, of, take } from 'rxjs';

import { ApiService } from 'src/app/@graphql/services/api.service';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';

import { ARTICLE_WARNINGS_BATCH_SIZE } from '../constants/inventory-jobs-service.constants';
import { ArticleInventoryWarning } from '../models/article-inventory-warning.model';
import { GetInventoryJobsParams } from '../models/get-inventory-jobs-params.model';
import {
  InventoryJob,
  InventoryJobCounts,
  InventoryRetryResult,
} from '../models/inventory-job.model';

/**
 * Apollo service for the inventory outbox admin panel (`inventory_outbox`
 * recovery surface). Backend contract: `inventoryJobs`, `inventoryJobCounts`,
 * `retryInventoryJob` in V1-Insight-Api's
 * `graphql/resolvers/v-one-insight/InventoryOutbox.js`.
 *
 * Cache policy: inherited from `ApiService.query()`, which hardcodes
 * `fetchPolicy: 'network-only'` for every query. No override is added here —
 * that policy is exactly what this panel needs, since a retry must never be
 * followed by a re-render of stale cached pending/failed rows.
 */
@Injectable({
  providedIn: 'root',
})
export class InventoryJobsService extends ApiService {
  private readonly cookieAuthService = inject(CookieAuthService);

  constructor() {
    super(inject(Apollo));
  }

  private getCompanyId(): number {
    const companyObject = this.cookieAuthService.getCompanyObject;
    return Number(companyObject?.Id_company) || 0;
  }

  /**
   * Lists inventory outbox jobs for the current company. `status` is
   * omitted from the request entirely when not passed, so the backend
   * treats it as "all statuses" rather than filtering on an empty value.
   */
  getJobs(params: GetInventoryJobsParams = {}): Observable<InventoryJob[]> {
    const company = this.getCompanyId();
    const { status, olderThanMinutes, limit, offset } = params;

    const variables: Record<string, unknown> = { company };
    if (status !== undefined) {
      variables['status'] = status;
    }
    if (olderThanMinutes !== undefined) {
      variables['olderThanMinutes'] = olderThanMinutes;
    }
    if (limit !== undefined) {
      variables['limit'] = limit;
    }
    if (offset !== undefined) {
      variables['offset'] = offset;
    }

    return this.query(INVENTORY_JOBS_QUERY, variables).pipe(
      map((result: { inventoryJobs: InventoryJob[] }) => result.inventoryJobs),
    );
  }

  getCounts(): Observable<InventoryJobCounts> {
    const company = this.getCompanyId();
    return this.query(INVENTORY_JOB_COUNTS_QUERY, { company }).pipe(
      map(
        (result: { inventoryJobCounts: InventoryJobCounts }) =>
          result.inventoryJobCounts,
      ),
    );
  }

  /**
   * Runs one outbox job synchronously. The result reflects the outcome of
   * the retry attempt, including a `status: 'failed'` payload — that is a
   * result, not a transport error, and must not be caught/rethrown here.
   * Network errors and `UNAUTHORIZED` GraphQL errors propagate unchanged.
   */
  retryJob(id: string): Observable<InventoryRetryResult> {
    const company = this.getCompanyId();
    return this.mutation(RETRY_INVENTORY_JOB_MUTATION, { company, id }).pipe(
      map(
        (result) =>
          (result as { retryInventoryJob: InventoryRetryResult })
            .retryInventoryJob,
      ),
    );
  }

  /**
   * Marks a reviewed failure as handled out of band so it stops feeding
   * `articleInventoryWarnings`. Same payload contract as `retryJob`: the
   * outcome travels in `status` ('dismissed' on success, 'not_found' when the
   * row is no longer `failed`) rather than as a thrown error.
   */
  dismissJob(id: string): Observable<InventoryRetryResult> {
    const company = this.getCompanyId();
    return this.mutation(DISMISS_INVENTORY_JOB_MUTATION, { company, id }).pipe(
      map(
        (result) =>
          (result as { dismissInventoryJob: InventoryRetryResult })
            .dismissInventoryJob,
      ),
    );
  }

  /**
   * Looks up unresolved inventory warnings for a set of articles. The
   * backend rejects a call with more than 100 `articleIds`, so the ids are
   * split into batches of at most 100 and the calls are merged into one
   * flat array. Empty input short-circuits without hitting the backend.
   */
  getArticleWarnings(
    company: number,
    articleIds: number[],
  ): Observable<ArticleInventoryWarning[]> {
    // Some legacy call sites hand over `id_articulo` as a string even though
    // the model types it as `number` (backend/GraphQL response shape drift).
    // The `articleInventoryWarnings` query requires `[Int!]!`, so coerce and
    // drop anything non-numeric here rather than at every caller.
    const ids = articleIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));

    if (ids.length === 0) {
      return of([]);
    }

    const batches: number[][] = [];
    for (let i = 0; i < ids.length; i += ARTICLE_WARNINGS_BATCH_SIZE) {
      batches.push(ids.slice(i, i + ARTICLE_WARNINGS_BATCH_SIZE));
    }

    const requests = batches.map((batch) =>
      this.query(ARTICLE_INVENTORY_WARNINGS_QUERY, {
        company,
        articleIds: batch,
      }).pipe(
        map(
          (result: { articleInventoryWarnings: ArticleInventoryWarning[] }) =>
            result.articleInventoryWarnings,
        ),
        // `this.query(...)` returns Apollo's `valueChanges`, which never
        // completes on its own. `forkJoin` only emits once every source
        // completes, so without `take(1)` this would never emit in
        // production (it only appeared to work in tests because `of(...)`
        // mocks complete immediately). Same idiom as
        // `register-provider.component.ts`'s `loadData()`.
        take(1),
      ),
    );

    return forkJoin(requests).pipe(map((results) => results.flat()));
  }
}
